import React, { useState, useEffect, useRef, useContext } from "react";
import * as echarts from "echarts";
import { useTranslation } from "react-i18next";
import { getBlockInfoFloodURL } from "../../../Services/EndPoints";
import { getData } from "../../../Services/Services";
import { Viewicon } from "../../../Components/IconComponents";
import BlockPopUp from "./BlockPopUp";
import DistrictBlockDetails from "./DistrictBlockDetails";
import { ThemeContext } from "../../..";

const HalfDoughnutChart = (props) => {
  const { theme } = useContext(ThemeContext);
  const { showViewIcon = false, showAffectedCount = false } = props;
  const echartsDom1 = useRef(null);
  const { t } = useTranslation();
  const dataValue = props?.dataval && props?.dataval[0];
  const [popUp, setPopUp] = useState(false);
  const [distBlockDetails, setDistBlockDetails] = useState([]);
  const [activeTab, setActiveTab] = useState("Affected");
  const showPopUp = () => {
    setPopUp(true);
  };
  const columns = React.useMemo(
    () => [
      {
        Header: "District Name",
        accessor: "dist_name",
        disableSortBy: true,
      },
      {
        Header: "Block Name",
        accessor: "block_name",
        disableSortBy: true,
      },
    ],
    []
  );
  const handleToggle = (props) => {
    setPopUp(!popUp);
  };

  const getLegendColor = (theme) => {
    return theme === "light" ? "#2B6591" : "#fff";
  };

  const total = dataValue?.total_blocks || 0;
  const affected = dataValue?.affected_blocks || 0;
  const partiallyAffected = dataValue?.partially_affected || 0;
  const nonAffected = total - (affected + partiallyAffected);
  const fetchData = async (apiKey) => {
    try {
      const response = await getData(
        getBlockInfoFloodURL
          .replace("TODATE", props.endDateStr)
          .replace("DISTID", 0)
          .replace("APIKEY", apiKey)
          .replace("FROMDATE", props.startDateStr)
      );
      setDistBlockDetails(response);
    } catch (error) {
      console.error("Error fetching block data:", error);
    }
  };

  useEffect(() => {
    if (popUp) {
      if (activeTab === "Affected") {
        fetchData("get_affected_blocks");
      } else {
        fetchData("get_partially_affected_blocks");
      }
    }
  }, [activeTab, popUp]);

  useEffect(() => {
    const myChart = echarts.init(echartsDom1.current);

    const options = {
      tooltip: {
        trigger: "item",
        confine: true,
      },
      legend: [
        {
          left: "-3%",
          top: "72%",
          textStyle: {
            color: getLegendColor(theme),
            fontSize: 11, // Decrease font size
          },
          selectedMode: false,
          formatter: () =>
            `${t("flooddisaster.doughnutchartlegend")} (${affected})`,
          data: [t("flooddisaster.doughnutchartlegend")],
        },
        {
          left: "38%",
          top: "72%",
          textStyle: {
            color: getLegendColor(theme),
            fontSize: 11, // Decrease font size
          },
          selectedMode: false,
          formatter: () =>
            `${t("flooddisaster.doughnutchartlegend2")} (${partiallyAffected})`,
          data: [t("flooddisaster.doughnutchartlegend2")],
        },
        {
          left: "center",
          top: "86%",
          textStyle: {
            color: getLegendColor(theme),
            fontSize: 11, // Decrease font size
          },
          selectedMode: false,
          formatter: () =>
            `${t("flooddisaster.doughnutchartlegend1")} (${nonAffected})`,
          data: [t("flooddisaster.doughnutchartlegend1")],
        },
      ],
      title: {
        show: false,
        text: total,
        textStyle: {
          fontSize: 14,
          color: "white",
        },
        subtext: t("flooddisaster.doughnutchartcentertxt"),
        subtextStyle: {
          fontSize: 16,
          color: "white",
        },
        left: "center",
        top: "center",
      },
      series: [
        {
          bottom: "20%",
          color: theme === "light" ? ["#59b3ff", "#0f5698", "#082a49"] : ["#E95060", "#EB7F00", "#327910"],
          type: "pie",
          radius: ["50%", "70%"],
          startAngle: 180,
          endAngle: 360,
          avoidLabelOverlap: false,
          label: {
            show: false,
            position: "center",
          },
          emphasis: {
            label: {
              show: false,
            },
          },
          labelLine: {
            show: false,
          },
          data: [
            { value: affected, name: t("flooddisaster.doughnutchartlegend") },
            {
              value: partiallyAffected,
              name: t("flooddisaster.doughnutchartlegend2"),
            },
            {
              value: nonAffected,
              name: t("flooddisaster.doughnutchartlegend1"),
            },
          ],
        },
      ],
    };

    myChart.setOption(options);

    const handleResize = () => {
      myChart.resize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      myChart.dispose();
    };
  }, [props.dataval, t, theme]);

  return (
    <div
      className="card"
      style={{ width: "100%", height: "100%", overflow: "hidden" }}
    >
      <div className="card-body">
        <div
          className="card-title"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <span>{props.title}</span>
          <span className="cursor-pointer" onClick={showPopUp}>
            {props.showViewIcon && <Viewicon />}{" "}
          </span>
        </div>
        <div
          ref={echartsDom1}
          style={{ height: "90%", width: "100%", marginTop: "-20px" }}
        />
      </div>
      {popUp && (
        <BlockPopUp
          title="Affected Block Details"
          show={popUp}
          handleToggle={handleToggle}
          component={
            <DistrictBlockDetails
              data={distBlockDetails}
              column={columns}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          }
        />
      )}
    </div>
  );
};

export default HalfDoughnutChart;
